import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormikValues } from 'formik';
import { useDispatch } from 'react-redux';

import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useAddRule from 'Hooks/query-hooks/global-rules/useAddRule';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useGetConclusionFlags from 'Hooks/query-hooks/useGetConclusionFlags';

import Layout from 'Components/Layout';
import AddRuleForm from 'Components/AddRulePage';

import { setGroup } from 'Store';

type CreateSimpleRuleLayoutProps = {
    parameterData: UseGetParametersResponse;
    listData: ListApiGetResponse;
};

const CreateSimpleRuleLayout = ({ parameterData, listData }: CreateSimpleRuleLayoutProps) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const { addModal } = useReduxModal();
    const history = useHistory();
    const formRef = React.useRef<FormikValues>();
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const dispatch = useDispatch();
    const { data: landlordInfo } = useGetLandlordInfo();
    const { data: conclusionFlagListData } = useGetConclusionFlags();

    const formInitialValues: NewRuleFormSchema = {
        ruleId: '',
        group: [],
        priority: 1,
        active: false,
        description: '',
        actions: [{ actionId: 0, operator: '', values: [] }],
        comment: '',
        conditions: [
            {
                // FE generates unique conditionIds to use it as keys of dynamically created Condition components
                // Each id determines which condition component to re-render when one of its states changes
                // It will be overwritten to meet BE requirements in the handleSave function
                conditionId: 0,
                parameterField: { name: [], type: '' },
                operatorField: [],
                valueField: '',
                valueType: '',
                readOnly: false
            }
        ],
        allowOverrides: false,
        hiddenFromTenant: false
    };

    const onSuccess = (_data: any, { ruleId }: any) => {
        dispatch(setGroup(''));
        addAlert({
            id: 'add-rule',
            content: t('addRuleSuccess', { ruleId }),
            status: 'success'
        });

        history.push('/global-rules');
    };

    const parseError = (err: any) => {
        const error = err.response;

        const customErrorMessage =
            error?.data?.error?.message || error?.data?.error?.msg?.error || error?.data?.error?.error?.message;

        return customErrorMessage
            ? t('addRuleErrorSpecific', { errMsg: customErrorMessage })
            : t('addRuleErrorGeneric');
    };

    const onError = (err: any) => {
        addModal({
            id: 'add-rule-error',
            status: 'error',
            title: t('Unable to save rule'),
            content: <>{parseError(err)}</>,
            cancelText: t('Close')
        });

        if (formRef.current) {
            formRef.current.setSubmitting(false);
        }
    };

    const mutation = useAddRule({
        templateRepositoryId: templateRepositoryId!,
        mutationOptions: {
            onSuccess,
            onError
        }
    });

    return (
        <Layout>
            <AddRuleForm
                mode='add'
                pageHeader={t('New Rule')}
                formRef={formRef}
                parameterData={parameterData}
                listData={listData}
                mutation={mutation}
                formInitialValues={formInitialValues}
                conclusionFlagListData={conclusionFlagListData}
                repoId={templateRepositoryId!}
                disabledInputFields={{
                    disableDefinitionChange: false,
                    disableOverrideChange: false,
                    hideOverrideChange: !landlordInfo?.isLandlord
                }}
            />
        </Layout>
    );
};

export default CreateSimpleRuleLayout;
